import React, { createContext, ReactNode, useContext, useEffect, useState } from "react"
import { EJobStatus, IJob, IJobListing, IStop } from "../entities/job/interface"
import ShiftContext, { IShiftContext } from "./ShiftContext"

interface IJobContext {
    currentDestinationJobs: IJobListing[] | null
    currentJobs: IJob[] | null
    currentDestinationIndex: number
    currentJobIndex: number
    groupId: string
    isConsecutive: boolean
    isRoadMap: boolean
    setIndexes: (indexes: IJobContextIndexes) => void
    setCurrentJobStatus: (jobStatus: EJobStatus) => void
    setCurrentDestinationJobStatuses: (jobStatus: EJobStatus) => void
    startNewJobPart: (jobId: string, type: string) => void
    setIsConsecutive: (isConsecutive: boolean) => void
    setIsRoadMap: (isRoadMap: boolean) => void
    currentStops: IStop[]
    currentMapState: boolean // Add the missing property
    setCurrentMapState: (mapState: boolean) => void // Fix the typo 'setCurrentMaoState' to 'setCurrentMapState'
    setCurrentJobs(jobs: IJob[]): void
    setCurrentDestinationIndex: (destinationIndex: number) => void
}

interface IJobContextProps {
    children: ReactNode
}

export interface IJobContextIndexes {
    groupId: string
    destinationIndex: number
    stopIndex: number
}

const JobContext = createContext<IJobContext | undefined>(undefined)

export const JobProvider = ({ children }: IJobContextProps) => {
    const shiftContext = useContext(ShiftContext)
    if (!shiftContext) {
        throw new Error("Shift details could not be populated. Please check your network connection")
    }

    const [currentDestinationJobs, setCurrentDestinationJobs] = useState<IJobListing[] | null>(null)
    const [currentJobs, setCurrentJobs] = useState<IJob[] | null>(null)
    const [currentDestinationIndex, setCurrentDestinationIndex] = useState<number>(
        parseInt(sessionStorage.getItem("currentDestinationIndex") || "0")
    )
    const [currentJobIndex, setCurrentJobIndex] = useState<number>(0)
    const [currentStops, setCurrentStops] = useState<IStop[]>([])
    const [isConsecutive, setIsConsecutive] = useState<boolean>(false)
    const [isRoadMap, setIsRoadMap] = useState<boolean>(false)
    const [groupId, setGroupId] = useState<string>(window.sessionStorage.getItem("groupId") ?? "")
    const [currentMapState, setCurrentMapState] = useState<boolean>(false)

    useEffect(() => {
        const screens = JSON.parse(sessionStorage.getItem("screens") || "{}")
        const isRoadMap = screens?.roadMap === "true" ? true : false
        setIsRoadMap(isRoadMap)

        const isConsecutive = screens?.consecutive === "true" ? true : false
        setIsConsecutive(isConsecutive)
    }, [isRoadMap, isConsecutive])

    function getCurrentJobsFromShift(groupId: string, currentDestinationIndex: number) {
        if (shiftContext?.stops && groupId) {
            const stops = shiftContext.stops[groupId]?.destinationStops[currentDestinationIndex] || []
            setCurrentStops(stops)
            const jobs = stops.map((stop) => shiftContext.jobs?.[stop.jobId]).filter(Boolean) as IJob[]
            setCurrentJobs(jobs)
        }
    }

    const setJobContextIndexes = ({ groupId, destinationIndex, stopIndex }: IJobContextIndexes) => {
        sessionStorage.setItem("groupId", groupId)
        sessionStorage.setItem("currentDestinationIndex", destinationIndex.toString())
        sessionStorage.setItem("currentStopIndex", stopIndex.toString())
        setGroupId(groupId)
        setCurrentDestinationIndex(destinationIndex)
        setCurrentJobIndex(stopIndex)
        getCurrentJobsFromShift(groupId, destinationIndex)
    }

    // Status setters
    const setCurrentJobStatus = async (jobStatus: EJobStatus) => {
        if (currentJobs?.length) {
            updateJob(currentJobs, jobStatus, shiftContext, setCurrentJobs)
        }
    }

    const startNewJobPart = async (jobId: string, type: string) => {
        if (!shiftContext?.jobs?.[jobId]) return

        let newJobStatus: EJobStatus

        switch (type) {
            case "unloaded":
                newJobStatus = EJobStatus.idle
                break
            case "pickup":
                newJobStatus = EJobStatus.onRouteToPickup
                break
            default:
                newJobStatus = EJobStatus.onRouteToDelivery
        }

        updateJob([shiftContext.jobs[jobId]], newJobStatus, shiftContext, setCurrentJobs, shiftContext.jobs[jobId])
    }

    const setCurrentDestinationJobStatuses = async (jobStatus: EJobStatus) => {
        if (!groupId || !shiftContext.stops?.[groupId]) return

        const destinationStops = shiftContext.stops[groupId]?.destinationStops[currentDestinationIndex]

        if (!destinationStops) return

        let updatedJobs = currentJobs ? [...currentJobs] : []

        destinationStops.forEach((stop) => {
            const job = shiftContext.jobs?.[stop.jobId]
            if (job) {
                updatedJobs = updateJob(updatedJobs, jobStatus, shiftContext, undefined, job)
            }
        })
    }

    useEffect(() => {
        if (groupId) {
            const stops = shiftContext.stops?.[groupId]?.destinationStops[currentDestinationIndex] || []
            const destinationJobs: any = stops
                .map((stop) => ({
                    ...shiftContext.jobs?.[stop.jobId],
                    type: stop.type,
                }))
                .filter(Boolean)
            setCurrentDestinationJobs(destinationJobs)
            const jobs = stops.map((stop) => shiftContext.jobs?.[stop.jobId]).filter(Boolean) as IJob[]
            setCurrentJobs(jobs)
        }
    }, [groupId, currentDestinationIndex, shiftContext.stops, shiftContext.jobs])

    return (
        <JobContext.Provider
            value={{
                currentDestinationJobs,
                currentJobs,
                currentDestinationIndex,
                currentJobIndex,
                groupId,
                isConsecutive,
                setIndexes: setJobContextIndexes,
                currentMapState,
                setCurrentJobStatus,
                setCurrentDestinationJobStatuses,
                startNewJobPart,
                setIsConsecutive,
                currentStops,
                setCurrentMapState: setCurrentMapState,
                setCurrentJobs,
                setCurrentDestinationIndex,
                isRoadMap,
                setIsRoadMap,
            }}
        >
            {children}
        </JobContext.Provider>
    )
}

export const useJobContext = () => {
    const context = useContext(JobContext)
    if (!context) {
        throw new Error("useJobContext must be used within a JobProvider")
    }
    return context
}

function updateJob(
    currentJobs: IJob[],
    jobStatus: EJobStatus,
    shiftContext: IShiftContext,
    setCurrentJobs?: (jobs: IJob[]) => void,
    job?: IJob
) {
    let updatedJobs: IJob[] = []

    if (currentJobs?.length > 0) {
        updatedJobs = job
            ? currentJobs.map((j: IJob) => (j._id === job._id ? { ...j, jobStatus } : j))
            : currentJobs.map((j) => ({ ...j, jobStatus }))
    } else if (job) {
        updatedJobs = [{ ...job, jobStatus }]
    }

    if (setCurrentJobs) {
        setCurrentJobs(updatedJobs)
    }

    shiftContext.updateJob(updatedJobs)

    return updatedJobs
}
